import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, OnDestroy, OnInit, Output, inject, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatSnackBar } from '@angular/material/snack-bar';
import { F1065SchB2Service } from './f1065-sch-b2.service';
import { forkJoin, Subscription } from 'rxjs';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import { buildHeaderDataShell, normalizeFilterParams, rowPayloadFromActiveScreen } from '../../../shared/workspace-element-context';

@Component({
    selector: 'gtw-f1065-sch-b2',
    templateUrl: './f1065-sch-b2.component.html',
    styleUrls: ['./f1065-sch-b2.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class F1065SchB2Component implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);

  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();
  private readonly subs = new Subscription();

  // Flags to prevent duplicate initialization (SuperPrompt V9)
  private isInitialized = false;
  private isLoading = false;

  // STANDARD COMPONENT PROPERTIES (SuperPrompt V9)
  currentTabName = "Form Entry";
  minimise = false;
  isSaveClicked = false;
  crudLoading = false;
  showFormLoader = true;

  // Component properties
  modalTitle = "SCHEDULE B-2- Election Out of the Centralized Partnership Audit Regime";
  title = "";
  scenarioLabel: string = "";
  entityList: any[] = [];
  activeTab = 1;
  tabs: any[] = [];

  // Data properties
  rowData: any = {};
  filterParams: any = {};
  globalService: any = {};
  GENERAL_CONFIG: any = {};
  baseURLs: any = {};
  processName: string = 'FORM_SAVE_1065_SCHB2';

  // Schedule B2 data structure
  scheduleB2Data: any = {};
  backupScheduleB2Data: any = {};
  part2List: any[] = [];
  multirow_data: any = {};
  multirow_data_backup: any = {};

  // Dropdown options
  part1DropDown = [
    { value: "I", label: "Individual (I)" },
    { value: "C", label: "Corporation (C)" },
    { value: "E", label: "Estate of Deceased Partner (E)" },
    { value: "F", label: "Eligible Foreign Entity (F)" },
    { value: "S", label: "S corporation (S)" }
  ];

  part2DropDown = [
    { value: "I", label: "Individual" },
    { value: "E", label: "Estate of Deceased Shareholder" },
    { value: "T", label: "Trust" },
    { value: "O", label: "Other" }
  ];

  constructor(
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService,
    private f1065SchB2Service: F1065SchB2Service,
    private _snackBar: MatSnackBar,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit() {
    this.tabs = [
      { id: 1, name: "Form Entry", active: true }
    ];
    this.applyWorkspaceContext();
    this.subs.add(this.gtwWorkspace.setTabActive.subscribe(() => {
      this.applyWorkspaceContext();
      this.tryInit();
    }));
    this.subs.add(this.gtwGlobal.globalParamsChanged$.subscribe(() => {
      this.applyWorkspaceContext();
      this.tryInit();
    }));
    this.tryInit();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const header = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    this.globalService = header;
    this.GENERAL_CONFIG = header.generalConfigData || {};
    this.filterParams = normalizeFilterParams(activeScreen);
    this.rowData = rowPayloadFromActiveScreen(activeScreen) || {};
    this.baseURLs = this.GENERAL_CONFIG;
    this.processName = this.globalService?.processName || 'FORM_SAVE_1065_SCHB2';
    this.scenarioLabel = this.filterParams?.scenarioLabel || this.filterParams?.scenario_label || this.globalService?.globalParams?.scenario_desc || '';

    if (this.filterParams?.parent_me_string) {
      this.globalService.globalParams.parent_me_string = this.filterParams.parent_me_string;
    }
  }

  // CRITICAL: Gate function to prevent duplicate initialization (SuperPrompt V9)
  private tryInit() {
    if (this.isInitialized || this.isLoading) {
      return;
    }

    if (!this.globalService || !this.GENERAL_CONFIG || !this.rowData || Object.keys(this.rowData).length === 0) {
      return;
    }

    this.isInitialized = true;
    this.init();
  }

  init() {
    try {
      if (this.rowData && Object.keys(this.rowData).length > 0) {
        this.setupEntityData();
        this.loadFormData();
      }
    } catch (e) {
      console.error('Error in component init:', e);
      this.showFormLoader = false;
      this.cdr.detectChanges();
    }
  }

  private setupEntityData() {
    // MANDATORY ORDER: Tax Year, LEID, LE Name (SuperPrompt V9)
    this.entityList = [
      { key: "Tax Year", value: this.rowData.TAX_YEAR },
      { key: "LEID", value: this.rowData.LEID },
      { key: "LE Name", value: this.rowData.LE_NAME }
    ];

    this.title = this.modalTitle + ' ( Tax Year : ' + this.rowData.TAX_YEAR + ' - Scenario : ' + this.scenarioLabel + ' )';
  }

  loadFormData() {
    if (this.isLoading) {
      return;
    }
    this.isLoading = true;
    this.showFormLoader = true;

    const params = {
      action_code: '9ha43j',
      activity_key: '6170',
      is_locked: '',
      jcd_key: this.rowData.JCD_KEY,
      jcd_ta_key: '5321',
      me_by_entity: '',
      scenario: this.rowData.SCENARIO_KEY,
      screen_type: 'grid',
      tax_year: this.rowData.TAX_YEAR,
      combination_key: this.rowData.COMBINATION_KEY,
      schedule_name: 'B2'
    };

    // Load both main data and shareholder data in parallel
    forkJoin({
      mainData: this.f1065SchB2Service.loadScheduleB2Data(
        this.GENERAL_CONFIG.base_url,
        params,
        this.globalService
      ),
      shareholderData: this.f1065SchB2Service.loadShareholderData(
        this.GENERAL_CONFIG.base_url,
        this.globalService?.globalParams || {},
        this.rowData
      )
    }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        if (response.mainData && response.mainData.jsonObject) {
          this.transformData(response.mainData.jsonObject);
        }
        if (response.shareholderData && response.shareholderData.jsonObject) {
          this.transformShareHolderData(response.shareholderData.jsonObject);
        }
        this.computeP3Value();
        this.showFormLoader = false;
        this.isLoading = false;
        this.cdr.detectChanges();
      },
      (error: any) => {
        console.error('Error loading form data:', error);
        this._snackBar.open('Error loading form data', 'Close', {
          duration: 4000,
          panelClass: ['error-snackbar']
        });
        this.showFormLoader = false;
        this.isLoading = false;
        this.cdr.detectChanges();
      }
    );
  }

  transformData(data: any[]) {
    this.scheduleB2Data = {};
    this.part2List = [];

    for (let i = 0; i < data.length; i++) {
      data[i].ATTRIB_VALUE = data[i].ATTRIB_VALUE || "";
      
      if (data[i]["PART_NO"] === "P2") {
        this.part2List.push(data[i]);
        continue;
      }
      
      if (!this.scheduleB2Data[data[i]["PART_NO"]]) {
        this.scheduleB2Data[data[i]["PART_NO"]] = [data[i]];
      } else {
        this.scheduleB2Data[data[i]["PART_NO"]].push(data[i]);
      }
    }

    this.scheduleB2Data["P2"] = {};

    // Check for S corporations to add Part 2 sections
    if (this.scheduleB2Data["P1"]) {
      for (let i = 2; i < this.scheduleB2Data["P1"].length; i += 3) {
        if (this.scheduleB2Data["P1"][i]["ATTRIB_VALUE"] === "S") {
          this.addPart2ForPartner(this.scheduleB2Data["P1"][i]["LEID"]);
        }
      }
    }

    this.backupScheduleB2Data = JSON.parse(JSON.stringify(this.scheduleB2Data));
  }

  transformShareHolderData(data: any[]) {
    this.multirow_data = this.multirow_data || {};

    data.forEach((shareholder: any) => {
      if (!this.multirow_data[shareholder.COMBINATION_KEY]) {
        this.multirow_data[shareholder.COMBINATION_KEY] = [];
      }

      this.multirow_data[shareholder.COMBINATION_KEY].push(
        { OCCURRENCE: shareholder.OCCURRENCE, ATTRIB_NAME: "SNAME", ATTRIB_VALUE: shareholder.SNAME },
        { OCCURRENCE: shareholder.OCCURRENCE, ATTRIB_NAME: "STIN", ATTRIB_VALUE: shareholder.STIN },
        { OCCURRENCE: shareholder.OCCURRENCE, ATTRIB_NAME: "STYPE", ATTRIB_VALUE: shareholder.STYPE }
      );
    });

    this.multirow_data_backup = JSON.parse(JSON.stringify(this.multirow_data));
  }

  private addPart2ForPartner(leid: string) {
    this.scheduleB2Data["P2"][leid] = [];
    for (let i = 0; i < this.part2List.length; i++) {
      if (this.part2List[i]["LEID"] === leid) {
        this.scheduleB2Data["P2"][leid].push(this.part2List[i]);
      }
    }
  }

  private removePart2ForPartner(leid: string) {
    delete this.scheduleB2Data["P2"][leid];
  }

  // Called when Part 1 dropdown changes
  part2Compute(leid: string, index: number) {
    if (this.scheduleB2Data['P1'][index + 2].ATTRIB_VALUE === "S") {
      this.addPart2ForPartner(leid);
    } else {
      this.removePart2ForPartner(leid);
    }
    this.computeP3Value();
    this.cdr.detectChanges();
  }

  addShareholder(partner: any) {
    if (!this.multirow_data) {
      this.multirow_data = {};
    }

    if (!this.multirow_data[partner.COMBINATION_KEY]) {
      this.multirow_data[partner.COMBINATION_KEY] = [];
    }

    const occurrenceNumber = this.multirow_data[partner.COMBINATION_KEY].length / 3;
    this.multirow_data[partner.COMBINATION_KEY].push(
      { OCCURRENCE: occurrenceNumber + 1, ATTRIB_NAME: "SNAME", ATTRIB_VALUE: "" },
      { OCCURRENCE: occurrenceNumber + 1, ATTRIB_NAME: "STIN", ATTRIB_VALUE: "" },
      { OCCURRENCE: occurrenceNumber + 1, ATTRIB_NAME: "STYPE", ATTRIB_VALUE: "" }
    );

    this.computeP3Value();
    this.cdr.detectChanges();
  }

  removeShareHolder(combinationKey: string, index: number) {
    this.multirow_data[combinationKey].splice(index, 3);
    this.computeP3Value();
    this.cdr.detectChanges();
  }

  private computeP3Value() {
    if (!this.scheduleB2Data['P3'] || !this.scheduleB2Data['P1']) {
      return;
    }

    // Line 1: Number of partners
    this.scheduleB2Data['P3'][0].ATTRIB_VALUE = Math.floor(this.scheduleB2Data['P1'].length / 3);
    
    // Line 2: Count of shareholders
    this.scheduleB2Data['P3'][1].ATTRIB_VALUE = 0;
    
    for (const key in this.scheduleB2Data['P2']) {
      if (this.scheduleB2Data['P2'].hasOwnProperty(key)) {
        this.scheduleB2Data['P3'][1].ATTRIB_VALUE = parseInt(this.scheduleB2Data['P3'][1].ATTRIB_VALUE) 
          + Math.floor((this.scheduleB2Data['P2'][key].length - 2) / 3);
      }
    }
    
    for (const key in this.multirow_data) {
      if (this.multirow_data.hasOwnProperty(key)) {
        this.scheduleB2Data['P3'][1].ATTRIB_VALUE = parseInt(this.scheduleB2Data['P3'][1].ATTRIB_VALUE) 
          + Math.floor(this.multirow_data[key].length / 3);
      }
    }

    // Line 3: Total (line 1 + line 2)
    this.scheduleB2Data['P3'][2].ATTRIB_VALUE = parseInt(this.scheduleB2Data['P3'][0].ATTRIB_VALUE) 
      + parseInt(this.scheduleB2Data['P3'][1].ATTRIB_VALUE);
  }

  validate(): boolean {
    let validationStatus = true;
    let stinValue: string;
    let occurrence: number;
    let ptrCombi: string;
    let ptrName: string;

    const data = this.buildSaveData();

    if (data[0].MULTIROW_DATA) {
      for (let j = 0; j < data[0].MULTIROW_DATA.length; j++) {
        ptrCombi = data[0].MULTIROW_DATA[j].COMBINATION_KEY;
        const matchingValue = data[0].VALUES.find((v: any) => v.COMBINATION_KEY === ptrCombi);
        ptrName = matchingValue ? matchingValue.LE_NAME : '';

        const stinArray = data[0].MULTIROW_DATA[j].JSON_DATA.filter((d: any) => d.ATTRIB_NAME === 'STIN');

        for (let i = 0; i < stinArray.length; i++) {
          stinValue = stinArray[i].ATTRIB_VALUE;
          occurrence = stinArray[i].OCCURRENCE;

          if (stinValue != null) {
            const x = stinValue.toString();
            if (x.length < 9 && x.length > 0) {
              validationStatus = false;
              this._snackBar.open(
                `Shareholder TIN must be 9 characters for Row Occurrence : ${occurrence} of S Corporation Partner : ${ptrName}.`,
                'Close',
                { duration: 5000, panelClass: ['error-snackbar'] }
              );
              break;
            }
          }
        }

        if (!validationStatus) {
          break;
        }
      }
    }

    return validationStatus;
  }

  private buildSaveData(): any[] {
    let tempArray = (this.scheduleB2Data["NA"] || [])
      .concat(this.scheduleB2Data["P1"] || [])
      .concat(this.scheduleB2Data["P3"] || []);

    for (const key in this.scheduleB2Data["P2"]) {
      if (this.scheduleB2Data["P2"].hasOwnProperty(key)) {
        tempArray = tempArray.concat(this.scheduleB2Data["P2"][key]);
      }
    }

    const saveData = [{
      COMBINATION_KEY: this.rowData.COMBINATION_KEY,
      LEID: this.rowData.LEID,
      LE_NAME: this.rowData.LE_NAME,
      VALUES: tempArray,
      MULTIROW_DATA: [] as any[]
    }];

    for (const key in this.multirow_data) {
      if (this.multirow_data.hasOwnProperty(key)) {
        const obj = {
          TAX_YEAR: this.rowData.TAX_YEAR,
          TRANS_TYPE_KEY: "1136",
          COMBINATION_KEY: key,
          LEID: this.rowData.LEID,
          GROUP_OBJ_KEY: 2020193,
          IRS_FORM_NO: "1065",
          SCHEDULE_NAME: "B2",
          PART_NO: "P2",
          SECTION: "NA",
          LINE_NO: "3",
          JSON_DATA: this.multirow_data[key]
        };
        saveData[0].MULTIROW_DATA.push(obj);
      }
    }

    return saveData;
  }

  save() {
    if (this.crudLoading) {
      this._snackBar.open('Please wait while we save this request', 'Close', {
        duration: 4000,
        panelClass: ['info-snackbar']
      });
      return;
    }

    if (!this.validate()) {
      return;
    }

    this.isSaveClicked = true;
    this.crudLoading = true;
    this.cdr.detectChanges();

    const saveData = this.buildSaveData();

    this.f1065SchB2Service.saveScheduleB2Form(
      this.GENERAL_CONFIG.base_url,
      saveData,
      this.globalService,
      this.filterParams,
      this.rowData,
      this.processName
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        this.isSaveClicked = false;
        this.crudLoading = false;

        if (response.callSuccess === "1" && response.errorMessage === "null") {
          this._snackBar.open('Form has been successfully saved/updated', 'Close', {
            duration: 4000,
            panelClass: ['success-snackbar']
          });
          this.requestGridUpdate.emit('refresh');
        } else {
          this._snackBar.open(response.errorMessage || 'Error occurred while saving', 'Close', {
            duration: 4000,
            panelClass: ['error-snackbar']
          });
        }
        this.cdr.detectChanges();
      },
      (error: any) => {
        this.isSaveClicked = false;
        this.crudLoading = false;
        this._snackBar.open('Error occurred while saving. Please try again.', 'Close', {
          duration: 4000,
          panelClass: ['error-snackbar']
        });
        this.cdr.detectChanges();
      }
    );
  }

  reset() {
    this.scheduleB2Data = JSON.parse(JSON.stringify(this.backupScheduleB2Data));
    this.multirow_data = JSON.parse(JSON.stringify(this.multirow_data_backup));
    this.cdr.detectChanges();
  }

  cancel() {
    this.requestGridUpdate.emit('cancel');
  }

  // Helper to get P2 keys for iteration
  getP2Keys(): string[] {
    return Object.keys(this.scheduleB2Data['P2'] || {});
  }

  // Helper to track by index for ngFor
  trackByIndex(index: number): number {
    return index;
  }
}

